/*
    Draft state for the campaign Budget screen, ported from the prototype's CampaignBudget.

    Nothing on this screen commits on blur. Every edit — segment dollars, margin, network and
    ad-group pauses, the spend pattern itself — is held as a draft and applied by Save, which
    runs its confirmations in a fixed order:

        floor block -> margin/budget confirm -> pause reallocation -> commit + log

    Cancelling any step keeps Edit open with the draft intact.
*/

import { useCallback, useMemo, useRef, useState } from "react"
import {
  D,
  Flight,
  Segment,
  SpendPattern,
  addDays,
  buildPattern,
  dayCount,
  effectiveMix,
  fmtRange,
  netAmount as computeNetAmount,
  reflowForNet,
  reflowMix,
  seg,
  splitAtToday,
} from "./segmentEngine"
import { FloorNetwork, adGroupOnIn, agKey, floorFailures, networkOnIn } from "./floors"

export type PauseScope = "none" | "period" | "flight"

export type PendingMap = Record<string, Record<string, boolean>>

export type PauseAsk = {
  kind: "net" | "ag"
  /** Which pattern + segment the toggle was made on. */
  pattern: SpendPattern
  segmentId: string
  names: string
  /** Set for an ad-group pause that zeroes out its network. */
  reason?: string
  networkKey?: string
  adGroupKeys?: string[]
  /**
   * Networks the freed share can actually go to, in the segment the toggle was made on.
   * Empty means Leave unallocated is the only honest answer, and the dialog says why.
   */
  reallocTo: string[]
  /**
   * The ceiling on that, for an ad-group pause: the networks that run the ad groups being
   * paused (10/8). Reallocating their share onto a network they do not run would spend it
   * on a different audience, which is not what Ops asked for. Absent for a network pause,
   * where any other active network is a valid target.
   */
  limitTo?: string[]
  scope: string
  pct: number
  amount: number
  even: boolean
}

export type BudgetChange = { label: string; from: number | null; to: number | null }

export type SaveConfirm =
  | { kind: "budget"; margin: { from: number; to: number; amountFrom: number; amountTo: number } | null; segments: BudgetChange[] }
  | { kind: "pause"; index: number }

export type LogEntry = { kind: string; summary: string }

export type BudgetDraftOptions = {
  flight: Flight
  /** The campaign's share of the engagement, set as a percent on the Campaigns list. */
  allocation: number
  savedMargin: number
  savedPattern: SpendPattern
  networks: FloorNetwork[]
  defaultMix: Record<string, number>
  fundedDays: number
  today: string
  /**
   * False until a campaign's first segment configuration has been saved. That first save is
   * setup rather than a change, so it logs nothing and prompts for no note.
   */
  configured: boolean
  /** Saved segments for the active pattern. Omitted, the pattern is seeded from scratch. */
  initialSegments?: Segment[]
  /**
   * Commit the draft. May be async: the server commits or rejects the whole campaign as a
   * unit, so a rejected save must not leave the screen looking saved. Reject the promise
   * and the draft stays open with everything still in it.
   */
  onCommit?: (
    entries: LogEntry[],
    store: Record<SpendPattern, Segment[]>,
    pattern: SpendPattern,
  ) => void | Promise<void>
  /** Set while a commit is in flight, so Save can show it and refuse a second click. */
  onSavingChange?: (saving: boolean) => void
}

const PATTERN_LABEL: Record<SpendPattern, string> = {
  even: "Even",
  monthly: "Monthly",
  custom: "Custom",
}

const CHOICE_TEXT: Record<PauseScope, string> = {
  none: "left unallocated",
  period: "reallocated for this period only",
  flight: "reallocated for the remainder of the flight",
}

const has = (o: Record<string, boolean> | undefined, k: string): boolean =>
  !!o && Object.prototype.hasOwnProperty.call(o, k)

const sortSegs = (list: Segment[]): Segment[] =>
  list.slice().sort((a, b) => D(a.start) - D(b.start))

export function useBudgetDraft(opts: BudgetDraftOptions) {
  const { flight, allocation, networks, defaultMix, fundedDays, today } = opts

  const [active, setActive] = useState<SpendPattern>(opts.savedPattern)
  const [draftActive, setDraftActive] = useState<SpendPattern>(opts.savedPattern)
  const [mode, setMode] = useState<SpendPattern>(opts.savedPattern)
  const [margin, setMarginState] = useState<number>(opts.savedMargin)
  const [editing, setEditing] = useState(false)
  const [confirm, setConfirm] = useState<SaveConfirm | null>(null)
  const [saving, setSavingState] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const setSaving = useCallback(
    (v: boolean) => {
      setSavingState(v)
      opts.onSavingChange?.(v)
    },
    [opts],
  )
  const [pauseChoices, setPauseChoices] = useState<PauseScope[]>([])
  /* Daily Cap edits, keyed by network. Campaign-wide rather than per window: a cap is a
     ceiling on the roll-forward a network applies within a day, not a share of this
     window's dollars, and the design keeps it outside the segment allocation for that
     reason. Only networks the operator actually touched appear here, so a Save writes one
     policy per edited network instead of converting every inherited split into an
     explicit one. */
  const [capEdits, setCapEdits] = useState<Record<string, string>>({})
  const [pendingNet, setPendingNet] = useState<PendingMap>({})
  const [pendingAg, setPendingAg] = useState<PendingMap>({})
  const [selected, setSelected] = useState<Partial<Record<SpendPattern, string>>>({})

  // The two inactive patterns are still built and kept reflowed, so previewing one in Edit
  // shows it at the current margin rather than at whatever it was seeded with.
  const [store, setStore] = useState<Record<SpendPattern, Segment[]>>(() => {
    const net = computeNetAmount(allocation, opts.savedMargin)
    const base: Record<SpendPattern, Segment[]> = {
      even: buildPattern("even", flight, net),
      monthly: buildPattern("monthly", flight, net),
      custom: buildPattern("custom", flight, net),
    }
    if (opts.initialSegments?.length) base[opts.savedPattern] = opts.initialSegments
    return base
  })

  const snapRef = useRef<{ margin: number; stores: Record<SpendPattern, Segment[]>; mode: SpendPattern } | null>(null)
  // Only segments the operator actually typed into are listed in the budget confirm; a reflow
  // moves every live segment, and listing those read as changes nobody made.
  const touched = useRef<Record<string, boolean>>({})

  const segments = store[mode]
  const selectedId = selected[mode] || segments[0]?.id
  const sel = segments.find((s) => s.id === selectedId) || segments[0]

  const marginPct = margin || 0
  const netAmount = computeNetAmount(allocation, marginPct)
  const mix = useMemo(() => effectiveMix(sel, defaultMix), [sel, defaultMix])

  const pk = mode + "|" + (sel?.id ?? "")

  const snapshot = useCallback(
    () => ({
      margin,
      stores: JSON.parse(JSON.stringify(store)) as Record<SpendPattern, Segment[]>,
      mode: draftActive,
    }),
    [margin, store, draftActive],
  )

  /* ---- segment edits ---- */

  const setSegments = useCallback(
    (fn: (list: Segment[]) => Segment[]) =>
      setStore((st) => ({ ...st, [mode]: fn(st[mode]) })),
    [mode],
  )

  const updateSegment = useCallback(
    (id: string, patch: Partial<Segment>) => {
      if (Object.prototype.hasOwnProperty.call(patch, "amount")) touched.current[mode + "|" + id] = true
      setSegments((list) => sortSegs(list.map((s) => (s.id === id ? { ...s, ...patch } : s))))
    },
    [mode, setSegments],
  )

  const removeSegment = useCallback(
    (id: string) => setSegments((list) => (list.length > 1 ? list.filter((s) => s.id !== id) : list)),
    [setSegments],
  )

  /** Only a neighbour's PINNED networks carry forward; auto ones pick up the default mix. */
  const inherit = (list: Segment[], start: string): Partial<Segment> | undefined => {
    const before = list.filter((s) => D(s.start) < D(start)).sort((a, b) => D(a.start) - D(b.start))
    const after = list.filter((s) => D(s.start) >= D(start)).sort((a, b) => D(a.start) - D(b.start))
    const nb = before.length ? before[before.length - 1] : after[0]
    return nb && nb.pinned ? { pinned: { ...nb.pinned } } : undefined
  }

  const addSegmentAt = useCallback(
    (date: string) => {
      const list = store[mode]
      let start = date
      if (D(start) < D(flight.start)) start = flight.start
      if (D(start) > D(flight.end)) start = flight.end

      let end = addDays(start, 13)
      const next = list.find((s) => D(s.start) > D(start))
      if (next && D(end) >= D(next.start)) end = addDays(next.start, -1)
      if (D(end) > D(flight.end)) end = flight.end
      if (D(end) < D(start)) end = start

      const s = seg(start, end, 0, inherit(list, start))
      setSegments((l) => sortSegs(l.concat([s])))
      setSelected((x) => ({ ...x, [mode]: s.id }))
    },
    [store, mode, flight, setSegments],
  )

  /**
   * "+ Add" appends after the last segment. With the flight fully allocated there is no free
   * stretch, so borrow up to 7 days off the last segment (leaving it at least one).
   */
  const addSegment = useCallback(() => {
    const list = store[mode]
    const last = list[list.length - 1]
    const gapStart = addDays(last.end, 2)
    if (D(gapStart) <= D(flight.end)) return addSegmentAt(gapStart)

    const adjacent = addDays(last.end, 1)
    if (D(adjacent) <= D(flight.end)) return addSegmentAt(adjacent)

    const borrow = Math.min(7, dayCount(last.start, last.end) - 1)
    if (borrow < 1) return
    const newStart = addDays(last.end, -(borrow - 1))
    const s = seg(newStart, last.end, 0, inherit(list, newStart))
    setSegments((l) =>
      sortSegs(
        l.map((x) => (x.id === last.id ? { ...x, end: addDays(newStart, -1) } : x)).concat([s]),
      ),
    )
    setSelected((x) => ({ ...x, [mode]: s.id }))
  }, [store, mode, flight, addSegmentAt, setSegments])

  /* ---- margin ---- */

  /**
   * Margin is campaign-level, not per-pattern, so all three stores reflow. Switching pattern
   * after a margin edit would otherwise land on segments generated at the old margin.
   */
  const setMargin = useCallback(
    (v: number) => {
      const before = margin || 0
      const after = v || 0
      setMarginState(v)
      if (after === before) return
      const net = computeNetAmount(allocation, after)
      setStore((st) => {
        const next = {} as Record<SpendPattern, Segment[]>
        ;(Object.keys(st) as SpendPattern[]).forEach((k) => {
          next[k] = reflowForNet(st[k], net, today).segments
        })
        return next
      })
    },
    [margin, allocation, today],
  )

  /* ---- network mix (pinning) ---- */

  /** The first edit to a network pins it on THIS segment only; others stay on auto. */
  const setNetworkPercent = useCallback(
    (key: string, pctValue: number) => {
      if (!sel) return
      updateSegment(sel.id, { pinned: { ...(sel.pinned || {}), [key]: pctValue } })
    },
    [sel, updateSegment],
  )

  /** Amount and Daily commit dollars; the segment total re-rolls from the sum. */
  const setNetworkSpend = useCallback(
    (key: string, dollars: number) => {
      if (!sel) return
      const current = effectiveMix(sel, defaultMix)
      const others = Object.keys(current)
        .filter((k) => k !== key)
        .reduce((a, k) => a + (sel.amount * (current[k] || 0)) / 100, 0)
      const total = others + dollars
      const pinned = { ...(sel.pinned || {}) }
      if (total > 0) pinned[key] = (dollars / total) * 100
      touched.current[mode + "|" + sel.id] = true
      updateSegment(sel.id, { amount: Math.round(total * 100) / 100, pinned })
    },
    [sel, defaultMix, mode, updateSegment],
  )

  /** A blank field clears the cap; the server reads "" as "no ceiling". */
  const setNetworkCap = useCallback((key: string, value: string) => {
    setCapEdits((prev) => ({ ...prev, [key]: value }))
  }, [])

  const unpin = useCallback(
    (key: string) => {
      if (!sel) return
      const pinned = { ...(sel.pinned || {}) }
      delete pinned[key]
      updateSegment(sel.id, { pinned })
    },
    [sel, updateSegment],
  )

  /* ---- pause toggles (pending until Save) ---- */

  const toggleNetwork = useCallback(
    (key: string, on: boolean, wasOn: boolean) => {
      if (!editing) return
      setPendingNet((p) => {
        const cur = { ...(p[pk] || {}) }
        if (on === wasOn) delete cur[key]
        else cur[key] = on
        return { ...p, [pk]: cur }
      })
    },
    [editing, pk],
  )

  const toggleAdGroup = useCallback(
    (groupKey: string, on: boolean, wasOn: boolean) => {
      if (!editing) return
      setPendingAg((p) => {
        const cur = { ...(p[pk] || {}) }
        if (on === wasOn) delete cur[groupKey]
        else cur[groupKey] = on
        return { ...p, [pk]: cur }
      })
    },
    [editing, pk],
  )

  const netName = (k: string) => networks.find((n) => n.key === k)?.name || k

  /** The name History shows for an ad group, from the `${network}|${adGroupId}` toggle key. */
  const agName = (key: string) => {
    const [networkKey, adGroupKey] = [key.slice(0, key.indexOf("|")), key.slice(key.indexOf("|") + 1)]
    const network = networks.find((n) => n.key === networkKey)
    return network?.adGroups.find((g) => g.key === adGroupKey)?.name || adGroupKey
  }
  const scopeOf = (m: SpendPattern, s: Segment) =>
    m === "even" ? "rest of flight" : fmtRange(s.start, s.end) + " only"

  /* ---- what Save will have to ask about ---- */

  const pauses = useMemo<PauseAsk[]>(() => {
    const out: PauseAsk[] = []

    Object.keys(pendingNet).forEach((key) => {
      const i = key.indexOf("|")
      const m = key.slice(0, i) as SpendPattern
      const id = key.slice(i + 1)
      const s = (store[m] || []).find((x) => x.id === id)
      if (!s) return
      const offKeys = Object.keys(pendingNet[key]).filter((k) => !pendingNet[key][k])
      if (!offKeys.length) return
      const mx = effectiveMix(s, defaultMix)
      const pct = offKeys.reduce((a, k) => a + (mx[k] || 0), 0)
      const reallocTo = networks
        .filter((n) => n.isConfigured && offKeys.indexOf(n.key) < 0 && networkOnIn(s, n))
        .map((n) => n.key)
      out.push({
        kind: "net",
        reallocTo,
        pattern: m,
        segmentId: id,
        names: offKeys.map(netName).join(", "),
        scope: scopeOf(m, s),
        pct,
        amount: (s.amount * pct) / 100,
        even: m === "even",
      })
    })

    /*
        A network whose last active ad group is paused in this save stops spending exactly as
        if the network itself were paused, so it gets the same 3-way choice. Skipped when the
        network is being paused directly — that is already one prompt.
    */
    Object.keys(pendingAg).forEach((key) => {
      const i = key.indexOf("|")
      const m = key.slice(0, i) as SpendPattern
      const id = key.slice(i + 1)
      const s = (store[m] || []).find((x) => x.id === id)
      if (!s) return
      const changes = pendingAg[key]
      const netChanges = pendingNet[key] || {}

      networks
        .filter((n) => n.isConfigured && n.adGroups.length)
        .forEach((n) => {
          if (!networkOnIn(s, n) || netChanges[n.key] === false) return
          const stillOn = n.adGroups.filter((g) => {
            const gk = agKey(n, g)
            return has(changes, gk) ? changes[gk] : adGroupOnIn(s, n, g)
          })
          if (stillOn.length) return
          const turnedOff = n.adGroups.filter((g) => changes[agKey(n, g)] === false)
          if (!turnedOff.length) return

          // Only the networks that run one of these ad groups, and still run it after this
          // save. A network that merely happens to be on does not inherit their money.
          const freed = turnedOff.map((g) => g.key)
          const limitTo = networks
            .filter((x) => x.isConfigured && x.key !== n.key)
            .filter((x) =>
              x.adGroups.some(
                (g) =>
                  freed.indexOf(g.key) >= 0 &&
                  (has(changes, agKey(x, g)) ? changes[agKey(x, g)] : adGroupOnIn(s, x, g)),
              ),
            )
            .map((x) => x.key)
          const reallocTo = limitTo.filter((k) => {
            const target = networks.find((x) => x.key === k)
            return !!target && netChanges[k] !== false && networkOnIn(s, target)
          })

          const mx = effectiveMix(s, defaultMix)
          const pct = mx[n.key] || 0
          out.push({
            kind: "ag",
            reallocTo,
            limitTo,
            pattern: m,
            segmentId: id,
            networkKey: n.key,
            adGroupKeys: turnedOff.map((g) => agKey(n, g)),
            names: n.name,
            reason:
              turnedOff.length === n.adGroups.length
                ? "its only ad group is being paused"
                : "its last active ad group is being paused",
            scope: scopeOf(m, s),
            pct,
            amount: (s.amount * pct) / 100,
            even: m === "even",
          })
        })
    })

    return out
  }, [pendingNet, pendingAg, store, networks, defaultMix])

  /* ---- floor block ---- */

  const floorFails = useMemo(() => {
    if (!editing) return []
    const bySeg = (src: PendingMap): PendingMap => {
      const out: PendingMap = {}
      Object.keys(src).forEach((key) => {
        const i = key.indexOf("|")
        if ((key.slice(0, i) as SpendPattern) !== draftActive) return
        out[key.slice(i + 1)] = src[key]
      })
      return out
    }
    return floorFailures(store[draftActive] || [], {
      networks,
      defaultMix,
      flight,
      fundedDays,
      today,
      pendingNet: bySeg(pendingNet),
      pendingAg: bySeg(pendingAg),
    })
  }, [editing, store, draftActive, networks, defaultMix, flight, fundedDays, today, pendingNet, pendingAg])

  /* ---- commit ---- */

  const applyPauses = (choices: PauseScope[]) => {
    const next: Record<SpendPattern, Segment[]> = { ...store }
    const entries: LogEntry[] = []

    /*
        10/8: a pause on an Even or Monthly campaign switches it to Custom from today, and
        the earlier days keep their pattern. The cut at today is what makes the second half
        of that true -- without it the pause would reach back over days that have already
        spent, and the whole rule exists to stop that.

        Triggered by the cut, not by "a pause happened", which is the same test the server
        applies to a margin edit (`CmdSaveLaunchBudget._pattern_after`). Even is one window
        over the flight and Monthly is one per month; once the window covering today is in
        two pieces neither description is true any more, so Custom names what is already on
        the screen. A pause on a campaign with nothing elapsed cuts nothing, leaves one
        window per month, and stays Monthly -- there are no earlier days to keep.
    */
    const touched = new Set<SpendPattern>()
    Object.keys(pendingNet)
      .concat(Object.keys(pendingAg))
      .forEach((key) => touched.add(key.slice(0, key.indexOf("|")) as SpendPattern))

    let patternTo = draftActive
    // Where a segment id lands after the cut: the pause belongs on the half that runs from
    // today, never on the elapsed half that keeps the id.
    const forward: Record<string, string> = {}

    if (touched.has(draftActive) && draftActive !== "custom") {
      const before = next[draftActive] || []
      const after = splitAtToday(before, today)
      if (after.length > before.length) {
        const ids = new Set(before.map((s) => s.id))
        const added = after.find((s) => !ids.has(s.id))
        const cut = before.find((s) => D(s.start) < D(today) && D(today) <= D(s.end))
        if (added && cut) forward[cut.id] = added.id
        // The layout moves with the pattern: Save sends `store[pattern]`, so leaving the cut
        // windows under "even" would send the uncut Even layout and lose the split.
        next.custom = after
        patternTo = "custom"
      }
    }

    /** The pattern a toggle's segments now live under, and that segment's id within it. */
    const land = (m: SpendPattern): SpendPattern => (m === draftActive ? patternTo : m)
    const landId = (id: string): string => forward[id] || id

    const onIn = (s: Segment, k: string): boolean => {
      if (has(s.netOn, k)) return (s.netOn as Record<string, boolean>)[k]
      const n = networks.find((x) => x.key === k)
      return !!n && n.active
    }

    pauses.forEach((p, idx) => {
      const choice = choices[idx] || "none"
      const list = next[land(p.pattern)] || []
      const base = list.find((x) => x.id === landId(p.segmentId))
      if (!base) return

      const offKeys =
        p.kind === "net"
          ? Object.keys(pendingNet[p.pattern + "|" + p.segmentId] || {}).filter(
              (k) => !(pendingNet[p.pattern + "|" + p.segmentId] || {})[k],
            )
          : [p.networkKey as string]

      entries.push({
        kind: p.kind === "ag" ? "Pause · ad group" : "Pause",
        summary:
          p.kind === "ag"
            ? `Ad Network "${netName(p.networkKey as string)}" has no active ad groups, ${CHOICE_TEXT[choice]}`
            : offKeys
                .map((k) => `Ad Network "${netName(k)}" paused, ${CHOICE_TEXT[choice]}`)
                .join("; "),
      })

      const realloc = (s: Segment): Record<string, number> => {
        const mx = effectiveMix(s, defaultMix)
        const pinned = { ...(s.pinned || {}) }
        // Recomputed per segment rather than taken from `reallocTo`, which answers for the
        // segment the toggle was made on. "Remainder of flight" reaches later segments, and
        // a network paused in one of those is not a target there.
        const activeKeys = networks
          .filter((n) => n.isConfigured && offKeys.indexOf(n.key) < 0 && onIn(s, n.key))
          .filter((n) => !p.limitTo || p.limitTo.indexOf(n.key) >= 0)
          .map((n) => n.key)
        const freed = offKeys.reduce((a, k) => a + (mx[k] || 0), 0)
        if (choice !== "none" && activeKeys.length && freed > 0) {
          const r = reflowMix(mx, offKeys, activeKeys)
          activeKeys.forEach((k) => (pinned[k] = r[k]))
        }
        offKeys.forEach((k) => (pinned[k] = 0))
        return pinned
      }

      next[land(p.pattern)] = list.map((s) => {
        if (s.id === landId(p.segmentId)) {
          const netOn = { ...(s.netOn || {}) }
          const agOn = { ...(s.agOn || {}) }
          if (p.kind === "net") offKeys.forEach((k) => (netOn[k] = false))
          else (p.adGroupKeys || []).forEach((g) => (agOn[g] = false))
          return { ...s, netOn, agOn, pinned: realloc(s) }
        }
        // "Remainder of flight" carries the same pause into every later segment.
        if (choice === "flight" && D(s.start) > D(base.start) && offKeys.some((k) => onIn(s, k))) {
          const netOn = { ...(s.netOn || {}) }
          const agOn = { ...(s.agOn || {}) }
          if (p.kind === "net") offKeys.forEach((k) => (netOn[k] = false))
          else (p.adGroupKeys || []).forEach((g) => (agOn[g] = false))
          return { ...s, netOn, agOn, pinned: realloc(s) }
        }
        return s
      })
    })

    // Resumes never silently restore an old share: the baseline has moved, so they come back
    // at 0% and Ops reallocates deliberately.
    Object.keys(pendingNet).forEach((key) => {
      const i = key.indexOf("|")
      const m = key.slice(0, i) as SpendPattern
      const id = key.slice(i + 1)
      const backOn = Object.keys(pendingNet[key]).filter((k) => pendingNet[key][k])
      if (!backOn.length) return
      next[land(m)] = (next[land(m)] || []).map((s) => {
        if (s.id !== landId(id)) return s
        const netOn = { ...(s.netOn || {}) }
        const pinned = { ...(s.pinned || {}) }
        backOn.forEach((k) => {
          netOn[k] = true
          pinned[k] = 0
        })
        return { ...s, netOn, pinned }
      })
      backOn.forEach((k) =>
        entries.push({
          kind: "Resume",
          summary: `Ad Network "${netName(k)}" resumed at 0%, share to be reallocated manually`,
        }),
      )
    })

    // Ad-group toggles that did not zero out a network commit straight through.
    Object.keys(pendingAg).forEach((key) => {
      const i = key.indexOf("|")
      const m = key.slice(0, i) as SpendPattern
      const id = key.slice(i + 1)
      const changes = pendingAg[key]
      const claimed = new Set(
        pauses.filter((p) => p.kind === "ag").flatMap((p) => p.adGroupKeys || []),
      )
      const rest = Object.keys(changes).filter((g) => !claimed.has(g))
      if (!rest.length) return
      next[land(m)] = (next[land(m)] || []).map((s) =>
        s.id !== landId(id) ? s : { ...s, agOn: { ...(s.agOn || {}), ...changes } },
      )
      rest.forEach((g) => {
        const j = g.indexOf("|")
        entries.push({
          kind: changes[g] ? "Resume" : "Pause · ad group",
          summary: `Ad Network "${netName(g.slice(0, j))}" ${
            changes[g] ? "resumed" : "paused"
          } for ad group "${agName(g)}"${changes[g] ? "" : ", left unallocated"}`,
        })
      })
    })

    return { next, entries, patternTo }
  }

  const finishSave = useCallback(
    async (choices: PauseScope[]) => {
      const before = snapRef.current
      // `patternTo` comes back from the apply, not from `draftActive`: a mid-flight pause
      // cuts the window at today and makes an Even or Monthly campaign Custom (10/8).
      const { next, entries, patternTo } = applyPauses(choices)

      const patternFrom = active

      const log: LogEntry[] = []
      if (opts.configured) {
        if (before && (before.margin || 0) !== marginPct) {
          log.push({
            kind: "Margin",
            summary: `Margin changed from ${before.margin}% to ${marginPct}%`,
          })
        }
        if (patternFrom !== patternTo) {
          log.push({
            kind: "Spend pattern",
            summary: `Spend pattern changed from ${PATTERN_LABEL[patternFrom]} to ${PATTERN_LABEL[patternTo]}`,
          })
        }
      }

      // The dialog closes either way -- the operator answered it -- but Edit mode and the
      // draft only clear once the server has taken the change. A rejected save leaves
      // everything exactly where it was, so the fix is one field away rather than retyped.
      setConfirm(null)
      setSaving(true)
      try {
        await opts.onCommit?.(opts.configured ? log.concat(entries) : [], next, patternTo)
      } catch (err) {
        setSaving(false)
        setSaveError(err instanceof Error ? err.message : String(err))
        return
      }
      setSaving(false)
      setSaveError(null)
      setActive(patternTo)
      setMode(patternTo)
      setStore(next)
      setPendingNet({})
      setPendingAg({})
      setCapEdits({})
      setPauseChoices([])
      setEditing(false)
    },
    [active, draftActive, marginPct, opts, applyPauses],
  )

  const choosePause = useCallback(
    (choice: PauseScope) => {
      const all = pauseChoices.concat([choice])
      if (all.length < pauses.length) {
        setPauseChoices(all)
        setConfirm({ kind: "pause", index: all.length })
        return
      }
      finishSave(all)
    },
    [pauseChoices, pauses.length, finishSave],
  )

  /** Save's step machine. Each confirm returns here with the step after the one it answered. */
  const proceedSave = useCallback(
    (step: number) => {
      if (floorFails.length) return

      if (step < 1) {
        const b = snapRef.current
        const marginMoved = !!b && (b.margin || 0) !== marginPct
        const marginChange = marginMoved
          ? {
              from: b!.margin || 0,
              to: marginPct,
              amountFrom: computeNetAmount(allocation, b!.margin || 0),
              amountTo: netAmount,
            }
          : null

        const beforeList = (b && b.stores[draftActive]) || []
        const afterList = store[draftActive] || []
        const byId: Record<string, Segment> = {}
        beforeList.forEach((s) => (byId[s.id] = s))

        const changes: BudgetChange[] = []
        afterList.forEach((s) => {
          const o = byId[s.id]
          const label = fmtRange(s.start, s.end)
          if (!o) changes.push({ label: label + " (added)", from: null, to: s.amount })
          else if (
            touched.current[draftActive + "|" + s.id] &&
            Math.round(o.amount * 100) !== Math.round(s.amount * 100)
          )
            changes.push({ label, from: o.amount, to: s.amount })
        })
        beforeList.forEach((s) => {
          if (!afterList.some((x) => x.id === s.id))
            changes.push({ label: fmtRange(s.start, s.end) + " (removed)", from: s.amount, to: null })
        })

        if (marginChange || changes.length) {
          setConfirm({ kind: "budget", margin: marginChange, segments: changes })
          return
        }
      }

      if (step < 2 && pauses.length) {
        setPauseChoices([])
        setConfirm({ kind: "pause", index: 0 })
        return
      }

      finishSave([])
    },
    [floorFails.length, marginPct, allocation, netAmount, draftActive, store, pauses.length, finishSave],
  )

  const beginEdit = useCallback(() => {
    snapRef.current = snapshot()
    touched.current = {}
    setDraftActive(active)
    setMode(active)
    setEditing(true)
  }, [snapshot, active])

  const cancelConfirm = useCallback(() => setConfirm(null), [])

  const cancelEdit = useCallback(() => {
    const b = snapRef.current
    if (b) {
      setStore(b.stores)
      setMarginState(b.margin)
      setDraftActive(b.mode)
      setMode(b.mode)
    }
    setPendingNet({})
    setPendingAg({})
    setCapEdits({})
    setPauseChoices([])
    setConfirm(null)
    setEditing(false)
  }, [])

  return {
    // committed vs draft
    active,
    draftActive,
    mode,
    editing,
    margin,
    marginPct,
    netAmount,

    segments,
    selected: sel,
    selectedId,
    store,
    mix,

    pendingNet: pendingNet[pk] || {},
    pendingAg: pendingAg[pk] || {},
    pauses,
    floorFails,
    confirm,
    saving,
    saveError,

    // viewing vs committing a pattern: switching tabs in Edit is preview only
    setMode,
    setActivePattern: setDraftActive,
    selectSegment: (id: string) => setSelected((x) => ({ ...x, [mode]: id })),

    setMargin,
    updateSegment,
    removeSegment,
    addSegment,
    addSegmentAt,
    setNetworkPercent,
    setNetworkSpend,
    setNetworkCap,
    capEdits,
    unpin,
    toggleNetwork,
    toggleAdGroup,

    beginEdit,
    cancelEdit,
    save: () => proceedSave(0),
    confirmBudget: () => proceedSave(1),
    choosePause,
    cancelConfirm,
  }
}
